/**
 * FormsTab — the study's forms (Phase C), inside StudyDetail. Lists forms
 * attached to this study with publish state + response counts; links into the
 * full builder at /crm/settings/forms/:id. Self-fetching (the study detail endpoint
 * doesn't carry forms), refreshed when the parent bumps reloadToken-style via
 * remount-free `onChanged` loads elsewhere — forms change rarely, so a fetch
 * per visit is fine.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, ClipboardList, Copy, Loader2, Plus, Search, Sparkles, X } from 'lucide-react';
import api from '../../../services/api';
import { CrmForm, StudyArtifact, StudyDetailResponse, studyInternalLabel } from '../../types';
import { relTime, useCanEditForms, studyHasExtractedDoc, FORM_SURFACES, formSurface, formSurfaceLabel, FormSurface } from '../../lib';
import { Alert, Badge, type BadgeTone, Z } from '../ui';
import { VersionHistoryButton } from '../versions';

// The artifact type tracking an AI form generation for each surface.
const FORM_ARTIFACT_TYPE: Record<FormSurface, string> = {
  landing: 'form_landing_draft',
  questionnaire: 'form_questionnaire_draft',
};
const MAX_AI_INSTRUCTIONS = 20000;

interface StudyChip { id: string | null; slug: string; title: string | null; internal_title?: string | null; }

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

function statusTone(status: string): BadgeTone {
  switch (status) {
    case 'active': return 'green';
    case 'closed': return 'primary';
    case 'archived': return 'gray';
    default: return 'amber';
  }
}

interface FormsTabProps {
  detail: StudyDetailResponse;
  isAdmin: boolean;
  onChanged: () => void;
}

export default function FormsTab({ detail, isAdmin, onChanged }: FormsTabProps) {
  const navigate = useNavigate();
  // Item #04 (owner): forms are recruiter-editable — full parity, not admin-only.
  const canEdit = useCanEditForms();
  const studyId = detail.study.id;
  const [forms, setForms] = useState<CrmForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Item #05: which group is being created ('landing' | 'questionnaire' | null).
  const [creating, setCreating] = useState<FormSurface | null>(null);
  // Item #05: which per-study pointer is mid-save ('landing_form_id' | 'questionnaire_form_id').
  const [designating, setDesignating] = useState<string | null>(null);

  // "Generate with AI": which group's direction panel is open, its free-text
  // direction, and the surface currently being kicked off (admin-only — the
  // generation is a full-document Bedrock run, same gate as the prescreen AI).
  const [aiPanel, setAiPanel] = useState<FormSurface | null>(null);
  const [aiInstructions, setAiInstructions] = useState('');
  const [aiBusy, setAiBusy] = useState<FormSurface | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const hasExtractedDoc = studyHasExtractedDoc(detail);

  // Form-generation artifacts ride the study detail the parent already polls
  // every 4s while anything is 'generating' — so live progress comes free.
  const formArtifacts = useMemo(
    () => detail.artifacts.filter(
      (a) => a.artifact_type === 'form_landing_draft' || a.artifact_type === 'form_questionnaire_draft',
    ),
    [detail.artifacts],
  );
  // Newest generation per form id (badges + retry affordance on the rows).
  const aiByForm = useMemo(() => {
    const m = new Map<string, StudyArtifact>();
    for (const a of formArtifacts) {
      const fid = a.generation_options?.form_id;
      if (!fid) continue;
      const prev = m.get(fid);
      if (!prev || new Date(a.created_at).getTime() > new Date(prev.created_at).getTime()) m.set(fid, a);
    }
    return m;
  }, [formArtifacts]);
  const surfaceGenerating = (surface: FormSurface) =>
    formArtifacts.some((a) => a.status === 'generating' && a.artifact_type === FORM_ARTIFACT_TYPE[surface]);

  // "Use existing form" picker — copy any cataloged form (either group) into
  // this study. pickerSurface is the TARGET group (which button opened it) —
  // the copy is forced into that group regardless of the source form's own
  // surface, which is what makes cross-group copying (landing -> questionnaire
  // or back) actually land where the user expects.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSurface, setPickerSurface] = useState<FormSurface>('questionnaire');
  const [catalog, setCatalog] = useState<CrmForm[]>([]);
  const [studyTitles, setStudyTitles] = useState<Record<string, string>>({});
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [copyingId, setCopyingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/crm/forms', { params: { study_id: studyId } });
      setForms(res.data?.forms || []);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [studyId]);

  // Fetch on mount, and refetch whenever the set of in-flight AI generations
  // changes: a start adds the placeholder row; a finish swaps in the filled
  // definition + AI title (the parent's 4s detail poll drives the flips).
  const generatingKey = useMemo(
    () => formArtifacts
      .filter((a) => a.status === 'generating')
      .map((a) => a.generation_options?.form_id || a.id)
      .sort()
      .join(','),
    [formArtifacts],
  );
  useEffect(() => { load(); }, [generatingKey, load]);

  // Kick off an AI-drafted form for a group. The backend creates the draft
  // form row immediately and fills it from the crm-worker; the parent reload
  // starts the detail poll that tracks it.
  const generateWithAi = async (surface: FormSurface) => {
    if (aiBusy) return;
    setAiBusy(surface);
    setError(null);
    try {
      await api.post(`/api/crm/studies/${studyId}/forms/ai-generate`, {
        surface,
        instructions: aiInstructions.trim() || undefined,
      });
      setAiPanel(null);
      setAiInstructions('');
      await Promise.all([load(), onChanged()]);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setAiBusy(null);
    }
  };

  // Retry a failed generation into the same (still empty) form row.
  const retryAi = async (form: CrmForm) => {
    if (retryingId) return;
    setRetryingId(form.id);
    setError(null);
    try {
      await api.post(`/api/crm/forms/${form.id}/ai-generate`, { mode: 'fresh' });
      await Promise.all([load(), onChanged()]);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setRetryingId(null);
    }
  };

  // Item #05: build a form in a specific group (landing vs questionnaire),
  // attached to this study, then open it in the builder.
  const createForSurface = async (surface: FormSurface) => {
    if (creating) return;
    setCreating(surface);
    setError(null);
    try {
      const label = surface === 'landing' ? 'landing page form' : 'study questionnaire';
      const res = await api.post('/api/crm/forms', {
        title: `${detail.study.title} — ${label}`,
        surface,
        study_id: studyId,
      });
      navigate(`/crm/settings/forms/${res.data.form.id}`);
    } catch (e: any) {
      setError(errMsg(e));
      setCreating(null);
    }
  };

  // Copy the study's published prescreen into a new questionnaire draft (no
  // AI — a deterministic vocabulary translation on the server), then open it
  // in the builder. Anything the translation could not carry over is written
  // into the draft's description (the builder's Description field).
  const [copyingPrescreen, setCopyingPrescreen] = useState(false);
  const prescreenVersion = detail.prescreen?.version || 0;
  const hasPrescreen = prescreenVersion > 0;
  const copyFromPrescreen = async () => {
    if (copyingPrescreen || creating) return;
    setCopyingPrescreen(true);
    setError(null);
    try {
      const res = await api.post(`/api/crm/studies/${studyId}/forms/from-prescreen`, {});
      navigate(`/crm/settings/forms/${res.data.form.id}`);
    } catch (e: any) {
      setError(errMsg(e));
      setCopyingPrescreen(false);
    }
  };

  // Item #05: designate this study's per-group pointer (landing_form_id /
  // questionnaire_form_id). PATCHes the study and refreshes the parent detail.
  const designate = async (field: 'landing_form_id' | 'questionnaire_form_id', value: string) => {
    setDesignating(field);
    setError(null);
    try {
      await api.patch(`/api/crm/studies/${studyId}`, { [field]: value || null });
      onChanged();
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setDesignating(null);
    }
  };

  const openPicker = async (surface: FormSurface) => {
    setPickerSurface(surface);
    setPickerOpen(true);
    setSearch('');
    setPickerError(null);
    setPickerLoading(true);
    try {
      const [formsRes, studiesRes] = await Promise.all([
        api.get('/api/crm/forms'),
        api.get('/api/crm/studies'),
      ]);
      // Archived forms aren't reusable templates; everything else is fair game —
      // including the OTHER group, since copying a landing form in as a
      // questionnaire (or vice versa) is exactly what this picker is for.
      setCatalog((formsRes.data?.forms || []).filter((f: CrmForm) => f.status !== 'archived'));
      const titles: Record<string, string> = {};
      for (const s of (studiesRes.data?.studies || []) as StudyChip[]) {
        if (s.id) titles[s.id] = studyInternalLabel(s);
      }
      setStudyTitles(titles);
    } catch (e: any) {
      setPickerError(errMsg(e));
    } finally {
      setPickerLoading(false);
    }
  };

  // Copy a chosen catalog form into THIS study (a new independent draft) in the
  // GROUP the picker was opened for — not necessarily the source form's own
  // group — then open it so the admin can tailor it before publishing.
  const copyIntoStudy = async (form: CrmForm) => {
    if (copyingId) return;
    setCopyingId(form.id);
    setPickerError(null);
    try {
      const res = await api.post(`/api/crm/forms/${form.id}/duplicate`, {
        study_id: studyId,
        surface: pickerSurface,
      });
      navigate(`/crm/settings/forms/${res.data.form.id}`);
    } catch (e: any) {
      setPickerError(errMsg(e));
      setCopyingId(null);
    }
  };

  const filteredCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter((f) => f.title.toLowerCase().includes(q));
  }, [catalog, search]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <ClipboardList className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          Forms
          <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({forms.length})</span>
        </h3>
      </div>

      {error && (
        <Alert tone="error" size="sm" icon={false} className="mb-3">{error}</Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : (
        // Item #05: two separately-built groups — the public ad landing-page form
        // and the texted study questionnaire — each with its own per-study
        // designation pointer and create flow.
        <div className="space-y-6">
          {FORM_SURFACES.map((group) => {
            const groupForms = forms.filter((f) => formSurface(f.surface) === group.value);
            const field = group.value === 'landing' ? 'landing_form_id' : 'questionnaire_form_id';
            const designatedId = (group.value === 'landing'
              ? detail.study.landing_form_id
              : detail.study.questionnaire_form_id) || '';
            const publishable = groupForms.filter((f) => f.status === 'active' && (f.version || 0) > 0);
            return (
              <section key={group.value}>
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{group.plural}</h4>
                  <div className="flex flex-wrap items-center gap-2">
                    {isAdmin && (
                      <button
                        onClick={() => {
                          setAiInstructions('');
                          setAiPanel((p) => (p === group.value ? null : group.value));
                        }}
                        disabled={!!aiBusy || surfaceGenerating(group.value) || !hasExtractedDoc}
                        title={!hasExtractedDoc ? 'Upload a protocol in Documents first' : undefined}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {surfaceGenerating(group.value)
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Sparkles className="h-3.5 w-3.5" />}
                        {surfaceGenerating(group.value) ? 'Generating…' : 'Generate with AI'}
                      </button>
                    )}
                    {canEdit && (
                      <button
                        onClick={() => createForSurface(group.value)}
                        disabled={!!creating}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-primary-600 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
                      >
                        {creating === group.value ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                        {group.value === 'landing' ? 'Build landing page form' : 'Build study questionnaire'}
                      </button>
                    )}
                    {canEdit && group.value === 'questionnaire' && (
                      <button
                        onClick={copyFromPrescreen}
                        disabled={copyingPrescreen || !!creating || !hasPrescreen}
                        title={hasPrescreen
                          ? `Copy the published prescreen (v${prescreenVersion}) questions and eligibility rules into a new questionnaire draft`
                          : 'Publish a prescreen on the Prescreen tab first'}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-primary-600 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
                      >
                        {copyingPrescreen ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ClipboardList className="h-3.5 w-3.5" />}
                        Copy from prescreen
                      </button>
                    )}
                    {canEdit && (
                      <button
                        onClick={() => openPicker(group.value)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        <Copy className="h-3.5 w-3.5" /> Use existing form
                      </button>
                    )}
                  </div>
                </div>
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  {group.value === 'landing'
                    ? 'Served on the public WCR ad page (/study/<slug>).'
                    : 'Texted to leads for the 1/2/3 respond-options menu. Without one, this study falls back to the network-wide questionnaire.'}
                </p>

                {/* AI direction panel — the prescreen builder's "direction for
                    the AI" pattern: optional free text, then kick off the
                    protocol-grounded draft. */}
                {aiPanel === group.value && isAdmin && (
                  <div className="mb-3 rounded-lg border border-primary-200 bg-primary-50/60 p-3 dark:border-primary-800 dark:bg-primary-900/10">
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      The AI drafts a {group.value === 'landing' ? 'landing page form' : 'study questionnaire'} from the study's protocol
                      {group.value === 'landing' ? ' — a short set of knock-out questions with eligibility rules.' : ' — a thorough self-report screening form with eligibility rules.'}
                      {' '}It opens in the builder for your review before publishing.
                    </p>
                    <textarea
                      value={aiInstructions}
                      onChange={(e) => setAiInstructions(e.target.value.slice(0, MAX_AI_INSTRUCTIONS))}
                      rows={2}
                      placeholder='Optional direction for the AI — e.g. "focus on cardiac exclusions" or "keep it under 6 questions"'
                      className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm placeholder-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    />
                    <div className="mt-2 flex items-center justify-end gap-2">
                      <button
                        onClick={() => { setAiPanel(null); setAiInstructions(''); }}
                        className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => generateWithAi(group.value)}
                        disabled={!!aiBusy}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50"
                      >
                        {aiBusy === group.value ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                        Generate
                      </button>
                    </div>
                  </div>
                )}

                {canEdit && (
                  <div className="mb-3">
                    <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                      {group.value === 'landing' ? 'Designated ad landing form' : 'Designated texted questionnaire'}
                    </label>
                    <select
                      value={designatedId}
                      disabled={designating === field}
                      onChange={(e) => designate(field, e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    >
                      <option value="">
                        {group.value === 'landing' ? 'None — ad landing page disabled' : 'None — use the network-wide questionnaire'}
                      </option>
                      {publishable.map((f) => <option key={f.id} value={f.id}>{f.title} (v{f.version})</option>)}
                    </select>
                    {designatedId && !publishable.some((f) => f.id === designatedId) && (
                      <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                        The designated form is not currently published — publish it below to activate it.
                      </p>
                    )}
                  </div>
                )}

                {groupForms.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No {group.plural.toLowerCase()} for this study yet.
                    {canEdit ? ` Build one${group.value === 'landing' ? ' (or import the WCR questionnaire from the Forms page)' : ''}.` : ''}
                  </p>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {groupForms.map((f) => {
                      const ai = aiByForm.get(f.id);
                      const aiGenerating = ai?.status === 'generating';
                      const aiFailed = ai?.status === 'failed';
                      // Retry re-runs mode:'fresh' (overwrites the definition), so
                      // it's only offered for a failed FRESH draft — the form is
                      // still empty. A failed revise left the form intact; the
                      // builder's "Ask AI to revise" panel is the retry there.
                      const canRetry = aiFailed && isAdmin && !ai?.generation_options?.ai_revise;
                      return (
                        <Link
                          key={f.id}
                          to={`/crm/settings/forms/${f.id}`}
                          className="flex items-center justify-between gap-3 py-3 transition-colors duration-100 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{f.title}</span>
                              <Badge size="sm" tone={statusTone(f.status)} className="shrink-0">
                                {f.status}
                              </Badge>
                              {f.id === designatedId && (
                                <span className="shrink-0 rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                  designated
                                </span>
                              )}
                              {aiGenerating && (
                                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                  <Loader2 className="h-3 w-3 animate-spin" /> AI {ai?.generation_options?.ai_revise ? 'revising' : 'drafting'}…
                                </span>
                              )}
                              {aiFailed && (
                                <span
                                  title={ai?.error || 'AI generation failed'}
                                  className="inline-flex shrink-0 items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                >
                                  <AlertTriangle className="h-3 w-3" /> AI failed
                                </span>
                              )}
                            </div>
                            <p className="mt-0.5 text-xs text-gray-400">
                              {f.version > 0 ? `v${f.version}` : 'never published'} · updated {relTime(f.updated_at)}
                            </p>
                          </div>
                          <div className="flex shrink-0 items-center gap-3">
                            {/* Inside the row's <Link>: the button prevents the
                                navigation itself and its modal is portaled. */}
                            {f.version > 0 && (
                              <VersionHistoryButton
                                kind="form"
                                params={{ form_id: f.id }}
                                title={f.title}
                                variant="icon"
                                label={`Version history — ${f.title}`}
                              />
                            )}
                            {canRetry && (
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); retryAi(f); }}
                                disabled={!!retryingId}
                                title="Retry the AI draft"
                                className="inline-flex items-center gap-1.5 rounded-lg border border-primary-600 px-2.5 py-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
                              >
                                {retryingId === f.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                                Retry
                              </button>
                            )}
                            <div className="text-right">
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{f.counts?.completed ?? 0}</p>
                              <p className="text-[11px] text-gray-400">completed</p>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {/* "Use existing form" picker — browse the whole catalog (both groups),
          copy one into this study AS the group the picker was opened for. */}
      {pickerOpen && (
        <div
          className={`fixed inset-0 ${Z.modalRaised} flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4`}
          onClick={() => setPickerOpen(false)}
        >
          <div
            className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-t-xl bg-white shadow-xl dark:bg-gray-800 sm:rounded-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Use an existing form"
          >
            <div className="flex items-start justify-between gap-2 border-b border-gray-200 px-5 py-3 dark:border-gray-700">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  Use an existing form as your {formSurfaceLabel(pickerSurface).toLowerCase()}
                </h3>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  Copies the form's questions &amp; settings into a new {formSurfaceLabel(pickerSurface).toLowerCase()} draft for this study — even
                  if the source is a {pickerSurface === 'landing' ? 'study questionnaire' : 'landing page form'}. The original is untouched.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPickerOpen(false)}
                aria-label="Close"
                className="shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="border-b border-gray-200 px-5 py-2.5 dark:border-gray-700">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search forms by title…"
                  className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-8 pr-3 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
              {pickerError && (
                <Alert tone="error" size="sm" icon={false} className="mx-3 mb-2">{pickerError}</Alert>
              )}
              {pickerLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              ) : filteredCatalog.length === 0 ? (
                <p className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  {catalog.length === 0 ? 'No forms in the catalog yet.' : 'No forms match your search.'}
                </p>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredCatalog.map((f) => {
                    const sourceStudy = f.study_id
                      ? (f.study_id === studyId ? 'This study' : studyTitles[f.study_id] || 'Another study')
                      : 'Unassigned';
                    return (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => copyIntoStudy(f)}
                        disabled={!!copyingId}
                        className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition-colors duration-100 hover:bg-gray-50 disabled:opacity-60 dark:hover:bg-gray-700/40"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{f.title}</span>
                            <Badge size="sm" tone={statusTone(f.status)} className="shrink-0">
                              {f.status}
                            </Badge>
                          </div>
                          <p className="mt-0.5 truncate text-xs text-gray-400">
                            {formSurfaceLabel(f.surface)}
                            {f.version > 0 ? ` · v${f.version}` : ' · never published'}
                            {` · ${sourceStudy}`}
                          </p>
                        </div>
                        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-primary-600 px-2.5 py-1 text-xs font-medium text-primary-700 dark:text-primary-400">
                          {copyingId === f.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
                          {copyingId === f.id ? 'Copying…' : 'Copy here'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
